import type { JSONContent } from '@tiptap/core';

import { ParagraphElement, type TParagraphElement } from '@/paragraphElement';
import type { TPostElement } from '@/postElement';
import type { SerializableNode } from '@/serializer';
import { SerializeTextLeaf, type TTextLeaf } from '@/textLeaf';
import type { ContentTag } from '@/types';

interface Props {
  tags: ContentTag[];
  node: JSONContent & SerializableNode;
  postNode?: TPostElement;
}

const ALLOWED_CATEGORY_GROUP_TYPES = ['free_selection', 'latest', 'archive'];

export const ContentTags = ({ tags, postNode, node, ...props }: Props) => {
  if (!tags || tags.length === 0) return null;

  const postsCategories = (postNode?.attrs?.postsCategory || '')
    .split(',')
    .map((c: string) => c.trim())
    .filter(Boolean);

  const postGroupType = postNode?.attrs?.postGroupType || '';
  const isAllowedCategoryGroupType = ALLOWED_CATEGORY_GROUP_TYPES.includes(postGroupType);

  const filteredTags =
    isAllowedCategoryGroupType && postsCategories.length > 0
      ? tags.filter((tag) => postsCategories.includes(tag?.display || ''))
      : tags;

  if (filteredTags.length === 0) return null;

  if (node?.attrs?.postTagsTruncateListEnabled) {
    const tagsCount = filteredTags.length;
    const tagsToShow = filteredTags[0];
    const tagsRemaining = tagsCount - 1;
    const textNode = {
      ...node?.content?.[0],
      text: tagsToShow.display,
    } as TTextLeaf;

    return (
      <div className="relative flex flex-wrap" {...props} style={{ gap: node?.attrs?.postTagsGap ?? '4px' }}>
        <ParagraphElement element={node as TParagraphElement}>
          <SerializeTextLeaf leaf={textNode} />
        </ParagraphElement>
        {tagsRemaining > 0 && (
          <ParagraphElement element={node as TParagraphElement}>
            <SerializeTextLeaf
              leaf={{
                ...textNode,
                text: `+${tagsRemaining}`,
              }}
            />
          </ParagraphElement>
        )}
      </div>
    );
  }

  return (
    <div className="relative flex flex-wrap" {...props} style={{ gap: node?.attrs?.postTagsGap ?? '4px' }}>
      {filteredTags.map((tag) => {
        const textNode = {
          ...node?.content?.[0],
          text: tag.display,
        } as TTextLeaf;

        return (
          <ParagraphElement key={tag.id} element={node as TParagraphElement}>
            <SerializeTextLeaf leaf={textNode} />
          </ParagraphElement>
        );
      })}
    </div>
  );
};
